// Live Core cards (shared/API.md §40, 2026-10-17). Twin of the native `CoreLinkCards` written from
// the same contract.
//
// A Core link in a message shows what that record looks like NOW. Pure parts: which link of a
// message is the Core one (first, host `core.satoritech.com.mx`, exactly as written), the answer's
// decode, the in-memory cache's re-ask rule (a null is cached too; re-ask only when a card older
// than its TTL is DRAWN again), the tone → colour token, and the «Actualizado hace N min» footer.
//
// §40.1 (2026-10-18): the same endpoint answers for ANY link. A Core link always gets a card
// (records as above; any other Core page or file → `kind: "page"`); any other link gets a plain web
// preview (`kind: "web"`) when Mattermost's own embed gives it none. Links inside code spans/blocks
// never get either.
import { t, tf } from '../i18n/i18n';

export const CORE_HOST = 'core.satoritech.com.mx';
/** At most this many distinct URLs per request (§40). */
export const CORE_BATCH_MAX = 20;
export const CORE_DEBOUNCE_MS = 150;
const DEFAULT_TTL_SECONDS = 60;

export type CoreTone = 'good' | 'warning' | 'danger' | 'neutral';

export interface CoreCard {
  kind: string;
  label: string;
  title: string;
  subtitle: string | null;
  status: { label: string; tone: CoreTone } | null;
  fields: Array<{ label: string; value: string }>;
  url: string;
  /** Epoch ms, or null. */
  updatedAt: number | null;
  ttlSeconds: number;
}

/** §40.1 `kind: "web"`: a plain preview for a non-Core link, drawn like an OpenGraph card. */
export interface WebCard {
  kind: 'web';
  site: string;
  title: string;
  description: string | null;
  /** Absolute third-party URLs — loaded only through the server image proxy (`proxiedImage`). */
  image: string | null;
  icon: string | null;
  url: string;
  ttlSeconds: number;
}

export type LinkCard = CoreCard | WebCard;

export function isWebCard(card: LinkCard | null | undefined): card is WebCard {
  return !!card && card.kind === 'web';
}

export interface CoreAnswer {
  /** url → card, or null = "no card for this reader" (never say why). */
  cards: Map<string, LinkCard | null>;
  ttlSeconds: number;
}

// URLs in message text: bare or inside Markdown `[text](url)` / `<url>`. Trailing sentence
// punctuation is not part of a pasted link.
const URL_RE = /https?:\/\/[^\s<>()\[\]"'`]+/gi;
const TRAILING = /[.,;:!?»”’)\]*_~]+$/;

// Code is not prose: a link inside a fenced block (``` or ~~~, closed by the same marker or the end
// of the message) or an inline `code span` is never previewed (§40.1).
const FENCE_RE = /^[ \t]{0,3}(`{3,}|~{3,})/;
const SPAN_RE = /(`+)[\s\S]*?\1/g;

/** The message with its code blocks and code spans blanked out. */
export function stripCode(message: string): string {
  if (!message.includes('`') && !message.includes('~~~')) return message;
  let fence: string | null = null;
  const prose = message.split('\n').map((line) => {
    const m = FENCE_RE.exec(line);
    if (fence) {
      if (m && m[1][0] === fence[0] && m[1].length >= fence.length) fence = null;
      return '';
    }
    if (m) {
      fence = m[1];
      return '';
    }
    return line;
  });
  return prose.join('\n').replace(SPAN_RE, ' ');
}

/** Every http(s) link outside code, in order, deduplicated, exactly as written. */
export function links(message: string): string[] {
  const out: string[] = [];
  for (const m of stripCode(message).matchAll(URL_RE)) {
    const raw = m[0].replace(TRAILING, '');
    let parsed: URL;
    try {
      parsed = new URL(raw);
    } catch {
      continue;
    }
    if ((parsed.protocol === 'http:' || parsed.protocol === 'https:') && parsed.hostname && !out.includes(raw)) out.push(raw);
  }
  return out;
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return '';
  }
}

/** Every Core URL in the message (outside code), in order, deduplicated, exactly as written. */
export function coreUrls(message: string): string[] {
  return links(message).filter((u) => hostOf(u) === CORE_HOST);
}

/** The message's Core link (one card per message, the first, like §12.4), or null. */
export function firstCoreUrl(message: string): string | null {
  return coreUrls(message)[0] ?? null;
}

/** A Mattermost permalink to a chat post (`/<team>/pl/<post id>`): its preview is Mattermost's own. */
export function isChatPermalink(url: string): boolean {
  try {
    return /^\/[^/]+\/pl\/[a-z0-9]{26}\/?$/i.test(new URL(url).pathname);
  } catch {
    return false;
  }
}

/**
 * The URL this message asks Core about (§40 + §40.1), or null:
 * - its first Core link (outside code) — a Core card, always (as §40);
 * - else its preview link (the first http(s) link, §12.4) when Mattermost's embed gives it no usable
 *   preview (`serverPreview` false), web previews are on, and the post's preview is not already
 *   suppressed (integration/bot attachments, a permalink to a chat post).
 */
export function previewUrl(message: string, opts: { serverPreview: boolean; suppressed: boolean; webPreviews: boolean }): string | null {
  const all = links(message);
  const core = all.find((u) => hostOf(u) === CORE_HOST);
  if (core) return core;
  if (opts.serverPreview || opts.suppressed || !opts.webPreviews) return null;
  const first = all[0];
  if (!first || isChatPermalink(first)) return null;
  return first;
}

/**
 * Where a web card's image or icon loads from: ALWAYS the server's image proxy on this client's own
 * base (`/api/v4/image?url=<percent-encoded absolute URL>`, session auth) — never the third-party
 * host (the reader's IP, the web CSP). A URL that is already the proxy is not wrapped again.
 */
export function proxiedImage(url: string | null | undefined, base: string): string | null {
  if (!url) return null;
  if (url.startsWith('/api/v4/image?')) return base + url;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
  if (parsed.pathname === '/api/v4/image' && parsed.searchParams.has('url')) return base + parsed.pathname + parsed.search;
  return `${base}/api/v4/image?url=${encodeURIComponent(url)}`;
}

function str(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function ttl(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : fallback;
}

const TONES: readonly CoreTone[] = ['good', 'warning', 'danger', 'neutral'];

function decodeWebCard(raw: Record<string, unknown>, fallbackTtl: number): WebCard | null {
  const title = str(raw.title)?.trim() ?? '';
  const site = str(raw.site)?.trim() ?? '';
  const url = str(raw.url) ?? '';
  if (!title && !site) return null;
  return {
    kind: 'web',
    site: site || hostOf(url),
    title,
    description: str(raw.description)?.trim() || null,
    image: str(raw.image) || null,
    icon: str(raw.icon) || null,
    url,
    ttlSeconds: ttl(raw.ttl_seconds, fallbackTtl),
  };
}

/**
 * One card; null when it has nothing to draw. `kind: "web"` → a WebCard (§40.1); anything else →
 * a CoreCard (records, and `kind: "page"`). Lenient: unknown tone = neutral. Each card's own
 * `ttl_seconds` wins; `fallbackTtl` (the answer's top level) only when it has none.
 */
export function decodeCard(raw: unknown, fallbackTtl = DEFAULT_TTL_SECONDS): LinkCard | null {
  if (!isObject(raw)) return null;
  if (raw.kind === 'web') return decodeWebCard(raw, fallbackTtl);
  const title = str(raw.title)?.trim() ?? '';
  if (!title) return null;
  let status: CoreCard['status'] = null;
  if (isObject(raw.status) && str(raw.status.label)) {
    const tone = str(raw.status.tone) as CoreTone | null;
    status = { label: raw.status.label as string, tone: tone && TONES.includes(tone) ? tone : 'neutral' };
  }
  const fields = Array.isArray(raw.fields)
    ? raw.fields
        .filter(isObject)
        .map((f) => ({ label: str(f.label) ?? '', value: f.value == null ? '' : String(f.value) }))
        .filter((f) => f.label || f.value)
    : [];
  const updated = str(raw.updated_at);
  const updatedAt = updated ? Date.parse(updated) : NaN;
  return {
    kind: str(raw.kind) ?? '',
    label: str(raw.label) ?? '',
    title,
    subtitle: str(raw.subtitle) || null,
    status,
    fields,
    url: str(raw.url) ?? '',
    updatedAt: Number.isFinite(updatedAt) ? updatedAt : null,
    ttlSeconds: ttl(raw.ttl_seconds, fallbackTtl),
  };
}

/** `200 {"cards":{url: card|null}, "ttl_seconds":60}`. Asked URLs the answer omits read as null. */
export function decodeAnswer(raw: unknown, asked: readonly string[]): CoreAnswer {
  const body = isObject(raw) ? raw : {};
  const ttlSeconds = ttl(body.ttl_seconds, DEFAULT_TTL_SECONDS);
  const cards = new Map<string, LinkCard | null>();
  const given = isObject(body.cards) ? body.cards : {};
  for (const url of asked) cards.set(url, decodeCard(given[url], ttlSeconds));
  return { cards, ttlSeconds };
}

export interface CacheEntry {
  card: LinkCard | null;
  /** When this answer arrived (epoch ms). */
  fetchedAt: number;
  ttlSeconds: number;
}

/**
 * Drawing a row with this URL: ask the server? Never asked → yes; asked and older than its TTL →
 * yes (it is on screen again); fresh → no. In flight or the session refused → no.
 */
export function shouldAsk(entry: CacheEntry | undefined, now: number, opts: { inFlight: boolean; disabled: boolean }): boolean {
  if (opts.disabled || opts.inFlight) return false;
  if (!entry) return true;
  return now - entry.fetchedAt >= entry.ttlSeconds * 1000;
}

/**
 * Folds an answer (or a failure) into the cache. A failed request keeps whatever was there — the
 * last card stays on screen — but stamps nothing, so the next draw may ask again.
 */
export function merge(cache: ReadonlyMap<string, CacheEntry>, answer: CoreAnswer | null, now: number): Map<string, CacheEntry> {
  const next = new Map(cache);
  if (!answer) return next;
  for (const [url, card] of answer.cards) next.set(url, { card, fetchedAt: now, ttlSeconds: card?.ttlSeconds ?? answer.ttlSeconds });
  return next;
}

/** Up to 20 distinct URLs for the next request, in the order they were asked. */
export function nextBatch(pending: Iterable<string>): string[] {
  const out: string[] = [];
  for (const url of pending) {
    if (out.includes(url)) continue;
    out.push(url);
    if (out.length === CORE_BATCH_MAX) break;
  }
  return out;
}

/** status.tone → the theme token the accent rule and the pill use (none = the accent). */
export function toneToken(tone: CoreTone | null | undefined): 'success' | 'warning' | 'mention' | 'accent' {
  switch (tone) {
    case 'good':
      return 'success';
    case 'warning':
      return 'warning';
    case 'danger':
      return 'mention';
    default:
      return 'accent';
  }
}

/** «Actualizado hace 5 min» (null when Core sent no time). */
export function updatedLabel(updatedAt: number | null, now: number): string | null {
  if (updatedAt == null) return null;
  const minutes = Math.max(0, Math.floor((now - updatedAt) / 60_000));
  if (minutes < 1) return t('web.coreCard.updatedNow');
  if (minutes < 60) return tf('web.coreCard.updatedMinutes', minutes);
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return tf('web.coreCard.updatedHours', hours);
  return tf('web.coreCard.updatedDays', Math.floor(hours / 24));
}

/** Fields two per row (§40: short label/value pairs). */
export function fieldRows<T>(fields: readonly T[]): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < fields.length; i += 2) rows.push(fields.slice(i, i + 2));
  return rows;
}

export const CoreLinkCards = { stripCode, links, coreUrls, firstCoreUrl, isChatPermalink, previewUrl, proxiedImage, isWebCard, decodeCard, decodeAnswer, shouldAsk, merge, nextBatch, toneToken, updatedLabel, fieldRows } as const;
